import {
  Button,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Stack,
  Text,
  useToast
} from '@homebotapp/hb-react-component-catalog'
import { useState } from 'react'
import { useDispatch } from 'react-redux'
import { deleteTeamMembership } from '../../../../store/slices/homebotNetwork/clientTeamMemberships'
import { defineMessages, useIntl } from 'react-intl'
import { AppDispatch } from '../../../../store'
import { formatCustomerProfileType } from '../../../../helpers/formatCustomerProfileType'
import { fetchClientData } from '../../../../actions/client'

interface ConfirmRemovePreferredPartnerModalProps {
  isOpen: boolean
  onClose: () => void
  membershipId?: string
  clientId: string
  onSuccess?: () => void
  customerType?: 'RealEstateAgent' | 'LoanOfficer' | 'InsuranceAgent' | 'TitleAgent'
}

export const MSG = defineMessages({
  confirmRemoveMessage: {
    id: 'confirmRemovePartner.confirmMessage',
    defaultMessage:
      'Are you sure you want to remove this {partnerType} from your Homebot team? They will no longer have access to your information or be able to communicate with you through Homebot.'
  },
  heading: {
    id: 'confirmRemovePartner.heading',
    defaultMessage: 'Remove {partnerType} from your Homebot team'
  },
  cancel: {
    id: 'confirmRemovePartner.cancel',
    defaultMessage: 'Cancel'
  },
  removeButton: {
    id: 'confirmRemovePartner.remove',
    defaultMessage: 'Remove {partnerType}'
  }
})

export const ConfirmRemovePreferredPartnerModal = ({
  isOpen,
  onClose,
  membershipId,
  clientId,
  onSuccess,
  customerType
}: ConfirmRemovePreferredPartnerModalProps) => {
  const [isDeleting, setIsDeleting] = useState(false)
  const toast = useToast()
  const dispatch = useDispatch<AppDispatch>()
  const intl = useIntl()

  // Get customer type-specific content
  const getContentForCustomerType = () => {
    const formattedType = formatCustomerProfileType(customerType || 'partner')
    return {
      heading: intl.formatMessage(MSG.heading, { partnerType: formattedType }),
      message: intl.formatMessage(MSG.confirmRemoveMessage, { partnerType: formattedType }),
      buttonText: intl.formatMessage(MSG.removeButton, { partnerType: formattedType }),
      successMessage: `${formattedType} removed successfully`
    }
  }

  const content = getContentForCustomerType()

  const handleDelete = async () => {
    if (!membershipId || !clientId) return

    setIsDeleting(true)
    try {
      const action = await dispatch(
        deleteTeamMembership({
          clientId,
          membershipId
        })
      )
      if (action.meta.requestStatus === 'fulfilled') {
        // refetch client data so the team view in the Intro component updates
        await dispatch(fetchClientData(clientId))
        toast({
          title: 'Success',
          description: content.successMessage,
          status: 'success'
        })

        onClose()
        setTimeout(() => {
          if (onSuccess) {
            onSuccess()
          }
        }, 100)
      } else if (action.meta.requestStatus === 'rejected') {
        throw new Error(action.payload as string)
      }
    } catch (error) {
      const formattedType = formatCustomerProfileType(customerType || 'partner').toLowerCase()

      toast({
        title: 'Error',
        description: `Failed to remove ${formattedType}. Please try again.`,
        status: 'error'
      })
    } finally {
      setIsDeleting(false)
    }
  }

  return (
    <>
      <Modal isOpen={isOpen} onClose={onClose} size='lg'>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>
            <Text size='lg' fontWeight='semibold' color='neutral.900' pr={3}>
              {content.heading}
            </Text>
          </ModalHeader>

          <ModalCloseButton />

          <ModalBody pb={6}>
            <Text color='neutral.700' size='sm'>
              {content.message}
            </Text>
          </ModalBody>

          <ModalFooter>
            <Stack direction='row' gap={2}>
              <Button
                variant='ghost'
                onClick={onClose}
                tracking={{
                  guid: 'fvKXGKUN9HgMhBwSgnhGhg',
                  ui_context: 'Cancel remove partner modal',
                  descriptor: 'Cancel remove partner from team'
                }}
              >
                {intl.formatMessage(MSG.cancel)}
              </Button>
              <Button
                colorScheme='danger'
                isLoading={isDeleting}
                onClick={handleDelete}
                tracking={{
                  guid: 'g9UoAWqBsbqEwfMQU67vHM',
                  ui_context: 'Remove partner modal',
                  descriptor: 'Remove partner from team'
                }}
              >
                {content.buttonText}
              </Button>
            </Stack>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  )
}
